import type { CatalogAssetMediaType } from "@/types/catalogAssets";

import { ACCEPTED_MIME } from "@/features/AssetUpload/validation";

/** Per-channel rules for `AssetPickerModal`. */
export type AssetPickerConstraints = {
  title: string;
  /** Asset types requested from the library; `isSelectable` narrows by MIME. */
  assetTypes: CatalogAssetMediaType[];
  isSelectable: (mediaType: string) => boolean;
  unsupportedTypeMessage: string;
  /** Cap applied at pick time, or `null` to not cap. */
  maxSizeBytes: number | null;
  uploadAccept: Record<string, string[]>;
  uploadHint: string;
  emptyLabel: string;
  /** Fixes up a dropped file before validation. */
  normalizeFile?: (file: File) => File;
};

const MIME_LABELS: Record<string, string> = {
  "image/jpeg": "JPEG",
  "image/jpg": "JPEG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WebP",
  "image/svg+xml": "SVG",
  "text/vcard": "vCard (.vcf)",
};

/**
 * Lists MIME types as readable names, e.g. "JPEG, PNG, or GIF". Unlabelled
 * types fall back to their uppercased subtype.
 */
export function formatMimeTypes(mimeTypes: readonly string[]): string {
  const labels = new Set(
    mimeTypes.map(
      (mime) => MIME_LABELS[mime] ?? mime.split("/")[1]?.toUpperCase() ?? mime,
    ),
  );
  return new Intl.ListFormat("en", { type: "disjunction" }).format(labels);
}

const imageAccept = Object.fromEntries(
  Object.entries(ACCEPTED_MIME).filter(([mime]) => mime.startsWith("image/")),
) as unknown as Record<string, string[]>;

const imageTypes = formatMimeTypes(Object.keys(imageAccept));

export const EDITOR_IMAGE_PICKER_CONSTRAINTS: AssetPickerConstraints = {
  title: "Select image",
  assetTypes: ["image"],
  isSelectable: (mediaType) => mediaType.startsWith("image/"),
  unsupportedTypeMessage: `Only images can be placed in the editor. Please select a ${imageTypes} image.`,
  // Uncapped: the asset is already in the library. Upload still enforces
  // MAX_IMAGE_BYTES.
  maxSizeBytes: null,
  uploadAccept: imageAccept,
  uploadHint: `Supported: ${imageTypes}`,
  emptyLabel: "No images in your library yet",
};
